"use client";

import { ComponentPlaceholderIcon } from "@radix-ui/react-icons";
import Cookie from "js-cookie";
import { FileText, LogOut } from "lucide-react";
import { signOut } from "next-auth/react";
import React, { type ReactNode } from "react";
import { useFeatureFlag } from "@/lib/feature-flags/client";

export interface UserMenuItem {
  label: string;
  icon: ReactNode;
  href?: string;
  onClick?: () => void;
  dividerAfter?: boolean;
}

export interface UserMenuItemsProps {
  onSelect?: (item: UserMenuItem) => void;
}

const onLogout = async () => {
  await signOut({
    redirect: false,
    callbackUrl: "/welcome",
  });

  Cookie.remove("selectedCommunity");
  Cookie.remove("organisationId");
  Cookie.remove("organisationName");
  Cookie.remove("organisationSlug");
  Cookie.remove("communityName");
  Cookie.remove("communityId");

  window.location.href = "/welcome";
};

// Define a hook to get user menu items with feature flag support
export const useUserMenuItems = (): UserMenuItem[] => {
  // Use the feature flag to check if billing section should be hidden
  const hideBillingSection = useFeatureFlag("hideBillingSection", false);

  const baseItems: UserMenuItem[] = [
    // Removed Profile, Settings, and Switch Org items as requested
  ];

  // Only include the billing item if the feature flag is not enabled
  if (!hideBillingSection) {
    baseItems.push({
      label: "Billing",
      icon: <ComponentPlaceholderIcon className="h-4 w-4 mr-2" />,
      href: "/admin/billing",
    });
  }

  // Add the remaining items
  baseItems.push(
    {
      label: "Request A Feature",
      icon: <FileText className="h-4 w-4 mr-2" />,
      href: "https://nudj.atlassian.net/servicedesk/customer/portal/3",
      dividerAfter: true,
    },
    {
      label: "Sign Out",
      icon: <LogOut className="h-4 w-4 mr-2" />,
      onClick: async () => {
        await onLogout();
      },
      dividerAfter: true,
    }
  );

  return baseItems;
};

// For backward compatibility
export const getUserMenuItems = (): UserMenuItem[] => {
  // This is a fallback for non-component context usage
  // Removed Profile, Settings, and Switch Org items as requested
  return [
    {
      label: "Billing",
      icon: <ComponentPlaceholderIcon className="h-4 w-4 mr-2" />,
      href: "/admin/billing",
    },
    {
      label: "Request A Feature",
      icon: <FileText className="h-4 w-4 mr-2" />,
      href: "https://nudj.atlassian.net/servicedesk/customer/portal/3",
      dividerAfter: true,
    },
    {
      label: "Sign Out",
      icon: <LogOut className="h-4 w-4 mr-2" />,
      onClick: async () => {
        await onLogout();
      },
      dividerAfter: true,
    },
  ];
};

export function renderUserMenuItems({
  items, // Remove default value to prevent hydration issues with hooks
  onSelect,
  renderItem,
}: {
  items: UserMenuItem[]; // Make this required
  onSelect?: (item: UserMenuItem) => void;
  renderItem: (item: UserMenuItem, index: number) => ReactNode;
}) {
  return items.map((item, index) => {
    const handleItemClick = () => {
      if (item.onClick) {
        item.onClick();
      }
      if (onSelect) {
        onSelect(item);
      }
    };

    return (
      <React.Fragment key={`${item.label}-${index}`}>
        {renderItem({ ...item, onClick: handleItemClick }, index)}
        {item.dividerAfter && <div className="my-1.5 h-px bg-border" />}
      </React.Fragment>
    );
  });
}
